<script setup lang="ts">
import LoginLayout from "@/layouts/LoginLayout.vue";
import {
  GoogleSignInButton,
  useOneTap,
  type CredentialResponse,
} from "vue3-google-signin";
import { createClient } from "@supabase/supabase-js";
import { useUserStore } from "@/stores/user";
import router from "@/router";
import { errorToast } from "@/utils/custom-toast";
import client from "@/utils/axios";

const supabase = createClient(
  process.env.VUE_APP_SUPABASE_URL || "",
  process.env.VUE_APP_SUPABASE_API_KEY || ""
);

// handle success event
const handleLoginSuccess = async (response: CredentialResponse) => {
  if (response.credential !== null) {
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: response.credential!,
    });
    if (error) {
      errorToast(error.message);
      return;
    }

    let userDetails = null;
    const { data: usersFilters, error: userError } = await supabase
      .from("users")
      .select(
        "search_filter, main_filter, status_filters, view_filters, name_filters,language"
      )
      .eq("id", data.user.id)
      .limit(1)
      .maybeSingle();

    if (userError) {
      errorToast(userError.message);
      return;
    }

    if (!usersFilters) {
      let identity = data?.user?.identities
        ? data?.user?.identities[0].identity_data
        : null;
      const { data: newUserFilters, error: newUserFiltersError } =
        await supabase
          .from("users")
          .insert({
            id: data?.user?.id,
            fullname: identity ? identity.full_name : "",
            email: identity ? identity.email : "",
          })
          .eq("id", data.user.id)
          .select()
          .maybeSingle();

      if (newUserFiltersError) {
        errorToast(newUserFiltersError.message);
        return;
      }
      userDetails = newUserFilters;
    } else {
      userDetails = usersFilters;
    }
    if (data && data.session) {
      let userStore = useUserStore();

      userStore.setUser(
        data?.user?.user_metadata?.full_name
          ? data.user.user_metadata.full_name
          : data.user.user_metadata.name,
        data?.user?.email,
        userDetails?.status_filters || [],
        userDetails?.main_filter || "",
        userDetails?.search_filter || "",
        userDetails?.view_filters || [],
        userDetails?.name_filters || [],
        userDetails?.language,
        data?.user?.user_metadata?.avatar_url
          ? data.user.user_metadata.avatar_url
          : ""
      );
      userStore.setTokens(
        data.session.access_token,
        data.session.refresh_token
      );

      const adminResponse = await client.get("/admin-translations");

      userStore.setAdminTranslation(adminResponse.data || []);

      const redirectPath =
        (router.currentRoute.value.query.redirect as string) || "/";

      router.push(redirectPath);
    }
  }
};

// handle an error event
const handleLoginError = () => {
  console.error("Login failed");
};

// initialize One Tap
useOneTap({
  onSuccess: handleLoginSuccess,
  onError: handleLoginError,
  cancelOnTapOutside: true,
  // options
});
</script>

<template>
  <LoginLayout>
    <div class="flex flex-col items-start">
      <!-- Smalt logo -->
      <div class="mb-[60px] md:mb-[118px]">
        <img src="@/assets/smalt-logo-login.svg" alt="Smalt logo" />
      </div>
      <!-- Main heading -->
      <h1
        class="text-[#FFFFFF] text-[42px] leading-[50px] tracking-[0.01em] pb-[41px] font-bold"
      >
        Be welcomed to <br />
        the <span class="text-[#C6C7F8]">QC-dash</span>
      </h1>
      <!-- Sign in -->
      <GoogleSignInButton
        shape="pill"
        @success="handleLoginSuccess"
        @error="handleLoginError"
      ></GoogleSignInButton>
    </div>
  </LoginLayout>
</template>
